import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { dirClass, dirLabel, fmtTime } from '../lib/format';
import { cx, Pill } from './ui';

/**
 * Floor feed card for a TradingView strategy signal (INT-SYN-02, D22). Signals reach the whole floor; a trader assigned
 * the symbol can make their own setup from one. A signal is a strategy event, not a trade instruction.
 */

export interface SignalView {
  id: string;
  source: { id: string; name: string };
  instrument: { id: string; symbol: string; market: string };
  symbol: string;
  strategy: string;
  event: 'GETTING_READY' | 'READY' | 'TRIGGERED' | 'INVALIDATED' | 'INFO';
  eventLabel: string;
  tf: string;
  timeframe: string;
  direction: 'LONG' | 'SHORT' | null;
  price: string | null;
  entry: string | null;
  stop: string | null;
  targets: string[];
  notes: string | null;
  signalTime: string | null;
  receivedAt: string;
  /** D27: the card flashes as New until this time (receivedAt + SIGNAL_FLASH_BARS bars of its timeframe). */
  flashUntil: string;
}

export interface SignalFeedEntry {
  id: string;
  type: string;
  createdAt: string;
  market: { id: string; code: string } | null;
  signal: SignalView & { canMakeSetup: boolean; mySetupId: string | null };
}

export const isSignalFeed = (i: object): i is SignalFeedEntry => 'signal' in i && !!(i as { signal?: unknown }).signal;

export const SIGNAL_TONE: Record<SignalView['event'], 'up' | 'warn' | 'accent' | 'down' | 'neutral'> = { TRIGGERED: 'up', READY: 'accent', GETTING_READY: 'warn', INVALIDATED: 'down', INFO: 'neutral' };

/** True while the signal is new (now < flashUntil). */
export const isFlashing = (s: Pick<SignalView, 'flashUntil'>, now: number) => !!s.flashUntil && now < Date.parse(s.flashUntil);

/**
 * The current time, re-read when the earliest of `until` passes (display only, D27: no DueItem). Pass every
 * flashUntil on screen; the component re-renders once per expiry, not on a fixed tick.
 */
export function useFlashClock(until: (string | null | undefined)[]) {
  const [now, setNow] = useState(() => Date.now());
  const next = until.reduce<number | null>((min, u) => {
    const t = u ? Date.parse(u) : NaN;
    return Number.isFinite(t) && t > now && (min === null || t < min) ? t : min;
  }, null);
  useEffect(() => {
    if (next === null) return;
    // setTimeout caps at ~24.8 days; longer waits just re-check then
    const t = setTimeout(() => setNow(Date.now()), Math.min(next - Date.now() + 50, 2 ** 31 - 1));
    return () => clearTimeout(t);
  }, [next]);
  return now;
}

function Fact({ k, v, cls }: { k: string; v: string; cls?: string }) {
  return (
    <span className="rounded-lg bg-raised px-2.5 py-1.5 text-[13px]">
      <span className="text-muted">{k}</span> <span className={cx('font-mono font-semibold', cls)}>{v}</span>
    </span>
  );
}

export function SignalFeedItem({ item: i, tz, onSymbol, now }: { item: SignalFeedEntry; tz: string; onSymbol?: (instrumentId: string) => void; now: number }) {
  const s = i.signal;
  const isNew = isFlashing(s, now);
  return (
    <article aria-label={`${isNew ? 'New signal' : 'Signal'}: ${s.instrument.symbol} ${s.strategy} ${s.eventLabel}`} data-flashing={isNew ? 'true' : undefined} className={cx('flex flex-col gap-3 rounded-xl border border-line bg-panel p-4', isNew && 'signal-flash')}>
      <div className="flex flex-wrap justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#2b4a78] bg-accent-bg font-mono text-[11px] font-semibold text-accent">
            TV
          </div>
          <div className="flex min-w-0 flex-col">
            <span>
              <strong>{s.source.name}</strong> <span className="text-sub">signalled</span>{' '}
              <strong>
                {onSymbol ? (
                  <button type="button" onClick={() => onSymbol(s.instrument.id)} className="cursor-pointer border-0 bg-transparent p-0 font-semibold text-ink hover:text-accent" title={`Show only ${s.instrument.symbol}`}>
                    {s.instrument.symbol}
                  </button>
                ) : (
                  s.instrument.symbol
                )}
              </strong>{' '}
              <span className="text-sub">{s.strategy}</span>
            </span>
            <span className="text-xs text-muted">
              {fmtTime(i.createdAt, tz, true)} · {s.instrument.market} · {s.symbol}
            </span>
          </div>
        </div>
        <span className="flex items-center gap-1.5 self-start">
          {isNew && (
            <Pill tone="accent" className="uppercase tracking-[0.06em]">
              New
            </Pill>
          )}
          <span className="rounded-md border border-line2 bg-raised px-2 py-0.5 text-xs text-sub">Signal</span>
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={SIGNAL_TONE[s.event]}>{s.eventLabel}</Pill>
        {s.direction && <Fact k="Side" v={dirLabel(s.direction)} cls={dirClass(s.direction)} />}
        {s.entry && <Fact k="Entry" v={s.entry} />}
        {s.stop && <Fact k="Stop" v={s.stop} cls="text-down" />}
        {s.targets.length > 0 && <Fact k={s.targets.length > 1 ? 'Targets' : 'Target'} v={s.targets.join(' / ')} />}
        {!s.entry && s.price && <Fact k="Price" v={s.price} />}
        <Fact k="TF" v={s.timeframe} />
      </div>
      {s.notes && <p className="m-0 leading-relaxed text-ink2">{s.notes}</p>}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        {s.mySetupId ? (
          <Link to={`/setups/${s.mySetupId}`} className="font-semibold">
            Open my setup →
          </Link>
        ) : s.canMakeSetup ? (
          <Link to={`/setups/new?signal=${s.id}`} className="font-semibold">
            Make a setup from this →
          </Link>
        ) : null}
        <span className="text-xs text-muted">TradingView strategy signal · not a trade instruction</span>
      </div>
    </article>
  );
}
